/**
 * Workflow Progress Summary Component
 *
 * Displays real-time execution progress for a workflow:
 * - Completion percentage and progress bar
 * - Node counts by status
 * - Currently running nodes
 */

import React from 'react';
import { Loader, CheckCircle2, XCircle, Clock, AlertTriangle } from 'lucide-react';

export default function WorkflowProgressSummary({ workflow, nodes }) {
  if (!workflow || !nodes || nodes.length === 0) {
    return null;
  }

  // Calculate statistics
  const totalNodes = nodes.length;
  const completedNodes = nodes.filter(n => n.data.status === 'completed').length;
  const runningNodes = nodes.filter(n => n.data.status === 'running').length;
  const failedNodes = nodes.filter(n => n.data.status === 'failed').length;
  const pendingNodes = nodes.filter(n => n.data.status === 'pending').length;
  const waitingApprovalNodes = nodes.filter(n => n.data.status === 'waiting_approval').length;

  const completionPercentage = totalNodes > 0
    ? Math.round((completedNodes / totalNodes) * 100)
    : 0;

  // Get currently running nodes
  const currentlyRunning = nodes
    .filter(n => n.data.status === 'running')
    .map(n => ({
      nodeKey: n.data.nodeKey,
      displayName: n.data.displayName,
      executionStartedAt: n.data.execution_started_at
    }));

  // Get nodes waiting for approval
  const waitingForApproval = nodes
    .filter(n => n.data.status === 'waiting_approval')
    .map(n => ({
      nodeKey: n.data.nodeKey,
      displayName: n.data.displayName,
      approvalPrompt: n.data.approvalPrompt || n.data.approval_prompt
    }));

  // Don't show for completed workflows
  if (workflow.status === 'completed') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 size={20} />
          <span className="font-medium">Workflow Completed</span>
        </div>
        <div className="text-sm text-green-600 mt-1">
          {completedNodes} of {totalNodes} nodes completed successfully
        </div>
      </div>
    );
  }

  // Don't show for failed workflows
  if (workflow.status === 'failed') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle size={20} />
          <span className="font-medium">Workflow Failed</span>
        </div>
        <div className="text-sm text-red-600 mt-1">
          {completedNodes} completed, {failedNodes} failed
        </div>
      </div>
    );
  }

  // Show for executing and paused workflows (but not completed/failed)
  if (workflow.status !== 'executing' && workflow.status !== 'paused') {
    return null;
  }

  const isPaused = workflow.status === 'paused';

  return (
    <div className={`border border-gray-200 rounded-lg shadow-sm p-4 mb-4 ${isPaused ? 'bg-yellow-50' : 'bg-white'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isPaused ? (
            <>
              <AlertTriangle size={18} className="text-yellow-600" />
              <h3 className="font-semibold text-gray-900">Workflow Paused - Waiting for Action</h3>
            </>
          ) : (
            <>
              <Loader size={18} className="text-blue-600 animate-spin" />
              <h3 className="font-semibold text-gray-900">Workflow Execution Progress</h3>
            </>
          )}
        </div>
        <div className={`text-2xl font-bold ${isPaused ? 'text-yellow-600' : 'text-blue-600'}`}>
          {completionPercentage}%
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div
          className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${completionPercentage}%` }}
        />
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-xs text-gray-600 mb-1">Total Nodes</div>
          <div className="text-lg font-semibold text-gray-900">{totalNodes}</div>
        </div>

        <div className="bg-green-50 rounded-lg px-3 py-2">
          <div className="text-xs text-green-600 mb-1">Completed</div>
          <div className="text-lg font-semibold text-green-700">{completedNodes}</div>
        </div>

        <div className="bg-blue-50 rounded-lg px-3 py-2">
          <div className="text-xs text-blue-600 mb-1">Running</div>
          <div className="text-lg font-semibold text-blue-700">{runningNodes}</div>
        </div>

        <div className={`rounded-lg px-3 py-2 ${waitingApprovalNodes > 0 ? 'bg-orange-50 border-2 border-orange-300' : 'bg-orange-50'}`}>
          <div className="text-xs text-orange-600 mb-1 font-medium">Waiting Approval</div>
          <div className="text-lg font-semibold text-orange-700">{waitingApprovalNodes}</div>
        </div>

        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-xs text-gray-600 mb-1">Pending</div>
          <div className="text-lg font-semibold text-gray-700">{pendingNodes}</div>
        </div>
      </div>

      {/* Nodes waiting for approval - MOST IMPORTANT to show */}
      {waitingForApproval.length > 0 && (
        <div className="border-t-2 border-orange-300 pt-3 bg-orange-50 -mx-4 -mb-4 px-4 pb-4 rounded-b-lg">
          <div className="text-sm font-bold text-orange-700 mb-2 flex items-center gap-2">
            <AlertTriangle size={16} className="animate-pulse" />
            Waiting for Your Approval ({waitingForApproval.length}):
          </div>
          <div className="space-y-2">
            {waitingForApproval.map((node) => (
              <div
                key={node.nodeKey}
                className="flex items-start gap-2 text-sm bg-white border-2 border-orange-300 rounded px-3 py-2"
              >
                <AlertTriangle size={16} className="text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{node.displayName}</div>
                  {node.approvalPrompt && (
                    <div className="text-xs text-gray-600 mt-1">{node.approvalPrompt}</div>
                  )}
                  <div className="text-xs text-orange-600 mt-1 font-medium">
                    → Click the node to approve or reject
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Currently running nodes */}
      {currentlyRunning.length > 0 && waitingForApproval.length === 0 && (
        <div className="border-t border-gray-200 pt-3">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Currently Running ({currentlyRunning.length}):
          </div>
          <div className="space-y-1">
            {currentlyRunning.map((node) => (
              <div
                key={node.nodeKey}
                className="flex items-center gap-2 text-sm text-gray-700 bg-blue-50 rounded px-2 py-1"
              >
                <Loader size={14} className="text-blue-600 animate-spin flex-shrink-0" />
                <span className="truncate">{node.displayName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failed nodes warning */}
      {failedNodes > 0 && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertTriangle size={16} />
            <span className="font-medium">{failedNodes} node(s) failed</span>
          </div>
        </div>
      )}
    </div>
  );
}
